import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { RenderIf } from 'react-rainbow-components';
import ListTile from 'components/ListTile';
import UserAvatar from 'components/UserAvatar';
import formatIntlPhoneNumber from 'data/services/phone/formatter';
import getDisplayName from 'data/services/profile/getDisplayName';
import getInitials from 'data/services/string/getInitials';
import { EntityGet } from 'data/firestore/types';
import { Customer } from 'data/firestore/agent/customer/types';
import useAgentData from 'hooks/useAgentData';
import { useNexhealthPatientLink } from 'customApps/nexhealth/hooks';
import { UserCellWrapper } from '../styled';
import { AvatarFrame, NexhealthMark } from './styled';

interface Props {
    row?: EntityGet<Customer>;
    disableLink?: boolean;
    showNexhealthLink?: boolean;
    onClick?: (customer: EntityGet<Customer>) => void;
}

const UserColumn = ({
    row, disableLink = false, showNexhealthLink = false, onClick,
}: Props) => {
    const {
        id = '', firstName = '', lastName = '', alias = '', displayName: displayNameProp = '', phoneNumber, photoURL,
    } = row || {};
    const { agentId = '' } = useParams();
    const { isAppEnabled } = useAgentData();
    const { data: linkResult } = useNexhealthPatientLink(
        agentId,
        id,
        showNexhealthLink && isAppEnabled('nexhealth'),
    );
    const [link] = linkResult?.data || [];
    const isNexhealthLinked = Boolean(link && !link.stale);
    const displayName = useMemo(
        () => getDisplayName({
            firstName,
            lastName,
            alias,
            displayName: displayNameProp,
        }),
        [firstName, lastName, alias, displayNameProp],
    );

    const initials = useMemo(() => {
        if (displayName === 'Unknown') return null;
        return getInitials(displayName);
    }, [displayName]);

    const LinkTo = useMemo(() => (disableLink ? undefined : `${id}/activity`), [disableLink, id]);

    const handleClick = useCallback(() => onClick?.(row as EntityGet<Customer>), [row, onClick]);

    return (
        <UserCellWrapper>
            <ListTile
                icon={(
                    <AvatarFrame>
                        <UserAvatar
                            initials={initials}
                            src={photoURL}
                            value={id}
                        />
                        <RenderIf isTrue={isNexhealthLinked}>
                            <NexhealthMark title="Linked to NexHealth" />
                        </RenderIf>
                    </AvatarFrame>
                )}
                to={LinkTo}
                title={displayName}
                onClick={onClick && handleClick}
                subtitle={formatIntlPhoneNumber(phoneNumber)}
            />
        </UserCellWrapper>
    );
};

export const CustomerColumn = (props: Props) => <UserColumn {...props} showNexhealthLink />;

export default UserColumn;
